import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { getBundles } from "@/api";
import { ROUTES } from "@/router/routes";
import type { Bundle } from "@/types";
import { Table } from "@/ui";
import { getBundlesTableColumns } from "./BundlesTableColumns";

interface BundlesTableProps {
  searchQuery: string;
}

export const BundlesTable = ({ searchQuery }: BundlesTableProps) => {
  const navigate = useNavigate();

  const { data: bundlesData, isLoading } = useQuery({
    queryKey: ["bundles", "getBundles"],
    queryFn: () => getBundles(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Filter bundles by search query
  const filteredBundles = useMemo(() => {
    return (bundlesData?.data ?? []).filter(
      (bundle) =>
        bundle.name.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
    );
  }, [bundlesData?.data, searchQuery]);

  const columns = useMemo(() => getBundlesTableColumns(), []);

  const table = useReactTable({
    data: filteredBundles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (row: Bundle) => {
    navigate(ROUTES.editBundle.replace(":bundleId", row.id));
  };

  return (
    <>
      <Table
        table={table}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <div className="flex items-center justify-between rounded-b-2xl border border-neutral-200 px-4 py-3">
        <span className="text-sm text-neutral-500">
          {table.getRowModel().rows.length}{" "}
          {table.getRowModel().rows.length === 1 ? "bundle" : "bundles"}
        </span>
      </div>
    </>
  );
};
